/**
 * Swipe View — slot/window bookkeeping (SMASH-1853).
 *
 * Spec §4.4: exactly three slots (active + one either side); off-screen
 * slides are paused, not destroyed, so a one-step back-swipe is instant.
 * Two steps back re-instantiates — that is accepted.
 *
 * Kept as pure functions (no React, no DOM) so slot-reuse stability — the
 * property the "instant one-step-back" and "iframe recycled in place" rules
 * both depend on — is unit-testable without mounting anything.
 */

/**
 * The desired track indices for a given `current` position: current ± radius,
 * clamped to [0, total-1]. Default radius 1 -> exactly 3 slides desired
 * (fewer at the ends of the track).
 */
export function desiredIndices(current, total, radius = 1) {
    const out = [];
    for (let i = current - radius; i <= current + radius; i += 1) {
        if (i >= 0 && i < total) out.push(i);
    }
    return out;
}

/**
 * Reassigns the fixed slot positions (array length = slot count) to the
 * desired track indices, keeping any slot that already holds a still-desired
 * index in place (stability is what lets an adapter's `recycle()` apply
 * instead of a destroy+rebuild, and what makes a one-step back-swipe show an
 * already-live slide instead of a freshly (re)built one).
 *
 * `prevAssignment` and the return value are both arrays of
 * `number | null` (track index, or unused) with a fixed length equal to the
 * slot count — never reordered, only mutated by value (a fresh array).
 */
export function syncSlotWindow(prevAssignment, desired) {
    const next = prevAssignment.map(() => null);
    const claimed = new Set();

    // Pass 1: keep any slot whose current occupant is still desired.
    prevAssignment.forEach((trackIndex, slotPos) => {
        if (trackIndex !== null && desired.indexOf(trackIndex) !== -1 && !claimed.has(trackIndex)) {
            next[slotPos] = trackIndex;
            claimed.add(trackIndex);
        }
    });

    // Pass 2: hand remaining desired indices to whichever slots are free.
    const freeSlotPositions = [];
    next.forEach((trackIndex, slotPos) => {
        if (trackIndex === null) freeSlotPositions.push(slotPos);
    });
    const remaining = desired.filter((trackIndex) => !claimed.has(trackIndex));
    remaining.forEach((trackIndex) => {
        const slotPos = freeSlotPositions.shift();
        if (slotPos !== undefined) next[slotPos] = trackIndex;
    });

    return next;
}
